import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { UserCheck, X } from 'lucide-react'
import { Button } from '../../../../components'
import {
  approveRegularization,
  getAttendanceRegularization,
  rejectRegularization,
} from '../api/hrmsAttendanceDetails.api'
import type { RegularizationRequest } from '../types/hrmsAttendanceDetails.types'
import type { StaffAttendanceRecord } from '../types/hrmsAttendanceRecords.types'
import { AttendanceDetailsCard } from './AttendanceDetailsCard'
import { AttendanceEmployeeCard } from './AttendanceEmployeeCard'
import { AttendanceTimeline } from './AttendanceTimeline'
import { RegularizationRequestCard } from './RegularizationRequestCard'
import { ReviewRegularizationModal } from './ReviewRegularizationModal'

interface AttendanceDetailsDrawerProps {
  record: StaffAttendanceRecord | null
  selectedDate: string
  onClose: () => void
  onReviewRegularization?: (id: string) => void
  onRegularizationApproved?: (request: RegularizationRequest) => void
  onRegularizationHandled?: (requestId: string) => void
}

export function AttendanceDetailsDrawer({
  record,
  selectedDate,
  onClose,
  onRegularizationApproved,
  onRegularizationHandled,
}: AttendanceDetailsDrawerProps) {
  const [loadingReg, setLoadingReg] = useState<boolean>(true)
  const [regularizationReq, setRegularizationReq] = useState<RegularizationRequest | null>(null)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState<boolean>(false)

  const effectiveDate = selectedDate || record?.date || ''

  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Body scroll lock & focus management
  useEffect(() => {
    if (!record) return

    previousFocusRef.current = document.activeElement as HTMLElement
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const timer = setTimeout(() => {
      closeButtonRef.current?.focus()
    }, 50)

    return () => {
      document.body.style.overflow = originalOverflow
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus()
      }
      clearTimeout(timer)
    }
  }, [record])

  // Escape key closing for drawer (disabled if review modal is open)
  useEffect(() => {
    if (!record) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isReviewModalOpen) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [record, isReviewModalOpen, onClose])

  // Fetch regularization request
  useEffect(() => {
    if (!record) return

    let isMounted = true
    setLoadingReg(true)

    // Pass the actual staff UUID instead of employee number
    const targetStaffId = record.staffId || record.rawRecord?.staffId || record.employeeId

    getAttendanceRegularization(targetStaffId, effectiveDate)
      .then((res) => {
        if (isMounted) {
          setRegularizationReq(res)
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoadingReg(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [record, effectiveDate])

  if (!record) return null

  const handleApprove = async (requestId: string) => {
    const updated = await approveRegularization(requestId)
    if (updated) {
      setRegularizationReq(updated)
      onRegularizationApproved?.(updated)
      onRegularizationHandled?.(requestId)
      onClose()
    }
  }

  const handleReject = async (requestId: string) => {
    const updated = await rejectRegularization(requestId)
    if (updated) {
      setRegularizationReq(updated)
      onRegularizationHandled?.(requestId)
      onClose()
    }
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="attendance-drawer-title"
      className="fixed inset-0 z-50 overflow-x-hidden"
    >
      {/* Overlay backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-xs transition-opacity"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div className="fixed top-0 right-0 z-10 h-screen w-full max-w-[420px] bg-white shadow-xl rounded-l-2xl flex flex-col overflow-hidden">
        {/* Sticky Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 z-10 p-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <UserCheck className="h-5 w-5 text-[#146971] shrink-0" />
            <h2 id="attendance-drawer-title" className="text-lg font-bold text-slate-900 truncate">
              Attendance Details
            </h2>
          </div>

          <Button
            ref={closeButtonRef}
            variant="secondary"
            size="sm"
            aria-label="Close"
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
            className="h-8 w-8 p-0 flex items-center justify-center border-none bg-transparent hover:bg-slate-100 text-slate-500 rounded-lg shrink-0"
          />
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <AttendanceEmployeeCard
            name={record.name}
            designation={record.designation}
            employeeId={record.employeeId}
            department={record.department}
          />

          <AttendanceDetailsCard record={record} selectedDate={effectiveDate} />

          <AttendanceTimeline record={record} />

          {loadingReg ? (
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-center">
              <p className="text-xs text-slate-500 font-medium">Loading...</p>
            </div>
          ) : regularizationReq ? (
            <RegularizationRequestCard
              request={regularizationReq}
              onReview={() => setIsReviewModalOpen(true)}
            />
          ) : null}
        </div>
      </div>

      {/* Review Regularization Modal */}
      <ReviewRegularizationModal
        isOpen={isReviewModalOpen}
        request={regularizationReq}
        record={record}
        selectedDate={effectiveDate}
        onClose={() => setIsReviewModalOpen(false)}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>,
    document.body
  )
}
